import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { useFinalizeVaultDocumentMutation } from "#graphql/supergraphHooks";
import {
  type FinalizeVaultDocumentInput,
  type FinalizeVaultDocumentMutation,
  type FinalizeVaultDocumentMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseFinalizeVaultDocumentReturn {
  finalizeVaultDocument: (
    input: FinalizeVaultDocumentInput,
  ) => Promise<FinalizeVaultDocumentMutation["finalizeVaultDocument"] | null>;
  loading: boolean;
  error: ReturnType<typeof useFinalizeVaultDocumentMutation>[1]["error"];
}

/**
 * DV-245 — step 3 of upload: after the bytes are PUT to the upload target,
 * activate the version and run guaranteed-attribute validation (moves the
 * document from `pending` to `validated` or `quarantined`). Usually driven via
 * {@link useUploadVaultDocument}.
 */
export const useFinalizeVaultDocument = (
  options?: MutationHookOptions<
    FinalizeVaultDocumentMutation,
    FinalizeVaultDocumentMutationVariables
  >,
): UseFinalizeVaultDocumentReturn => {
  const [mutate, { loading, error }] = useFinalizeVaultDocumentMutation(options);

  const finalizeVaultDocument = useCallback(
    async (input: FinalizeVaultDocumentInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.finalizeVaultDocument ?? null;
    },
    [mutate],
  );

  return { finalizeVaultDocument, loading, error };
};
